import { Button, Form, Input, InputNumber, Modal, Select, Space } from "antd";
import { useEffect } from "react";

import type { Group, SellerGroup } from "../model/types";

interface Props {
  open: boolean;
  selectedGroupSeller?: SellerGroup;
  setSelectedGroupSeller: (groupSeller: SellerGroup | undefined) => void;
  sellerGroups: SellerGroup[];
  group?: Group;
  onClose: () => void;
  onSave: (value: number, unit: "seconds" | "minutes" | "hours") => void;
}

const convertWaitSeconds = (seconds: number) => {
  if (seconds % 3600 === 0 && seconds > 0) {
    return {
      value: seconds / 3600,
      unit: "hours" as const,
    };
  }

  if (seconds % 60 === 0 && seconds > 0) {
    return {
      value: seconds / 60,
      unit: "minutes" as const,
    };
  }

  return {
    value: seconds,
    unit: "seconds" as const,
  };
};

export default function ResponseTimeModal({
  open,
  group,
  selectedGroupSeller,
  setSelectedGroupSeller,
  sellerGroups,
  onClose,
  onSave,
}: Props) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && group) {
      const waitTime = convertWaitSeconds(group.waitSeconds ?? 15 * 60);

      form.setFieldsValue({
        value: waitTime.value,
        unit: waitTime.unit,
      });
    }
  }, [open, group, form]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      onSave(values.value, values.unit);
    } catch {
      // Keep save blocked and let Ant Design show the inline validation error
    }
  };

  return (
    <Modal
      open={open}
      width={480}
      footer={null}
      onCancel={onClose}
      centered
      title={
        <div style={{ fontSize: 20, fontWeight: 700 }}>
          応答時間設定
          <span style={{ fontSize: 14, marginLeft: 8, fontWeight: 400 }}>
            （このグループのみ）
          </span>
        </div>
      }
    >
      <div style={{ padding: "16px 0 0 0" }}>
        <p style={{ color: "#4B5563", marginBottom: 24, fontSize: 14 }}>
          この設定は、現在選択中のグループ「{group?.name}」にのみ適用されます。
        </p>

        <Form form={form} layout="vertical">
          <Form.Item label="対象グループ">
            <Input disabled value={`${group?.name}（変更不可）`} />
          </Form.Item>

          <Form.Item label="担当セリ人グループ">
            <Select
              value={selectedGroupSeller?.id || ""}
              showSearch
              allowClear
              options={sellerGroups.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => {
                if (!value) {
                  setSelectedGroupSeller(undefined);
                  return;
                }
                const selectedSellerGroup = sellerGroups.find(
                  (item) => item.id === value,
                );
                if (selectedSellerGroup) {
                  setSelectedGroupSeller(selectedSellerGroup);
                }
              }}
            />
          </Form.Item>

          <Form.Item label="応答までの待機時間" style={{ marginBottom: 0 }}>
            <Space.Compact block>
              <Form.Item
                name="value"
                style={{ width: "65%", marginBottom: 0 }}
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  { required: true, message: "数値を入力してください" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const unit = getFieldValue("unit");

                      if (
                        unit === "seconds" &&
                        value !== undefined &&
                        value < 60
                      ) {
                        return Promise.reject(
                          new Error("秒単位では60以上を入力してください"),
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>

              <Form.Item
                name="unit"
                style={{ width: "35%", marginBottom: 0 }}
                rules={[{ required: true, message: "単位を選択してください" }]}
              >
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  options={[
                    { value: "seconds", label: "秒" },
                    { value: "minutes", label: "分" },
                    { value: "hours", label: "時間" },
                  ]}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Form>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            marginTop: 32,
          }}
        >
          <Button onClick={onClose} style={{ width: 120 }}>
            キャンセル
          </Button>

          <Button type="primary" onClick={handleSave} style={{ width: 120 }}>
            保存
          </Button>
        </div>
      </div>
    </Modal>
  );
}
